/* eslint-disable no-nested-ternary */
"use client";

import { baseCardCls, disableCls, noBtnCls, shadowCls, yesBtnCls } from "@/components/platform/style";
import VolRect from "@/components/platform/vol-rect";
import { useTradePanel } from "@/components/trade-panel/hooks/use-trade-panel";
import { OrderType } from "@/components/trade-panel/types";
import { SmartImage } from "@/components/ui/smart-image";
import { type DOMInfo, useCollectDOMInfo } from "@/hooks/useCollectDOMInfo";
import { useParseText } from "@/hooks/useParseText";
import { cn } from "@/lib/utils";
import { isEventExpired, isGuessExpired } from "@/utils/expiration";
import { formatCentPriceAsChancePercent, formatPriceAsOddsMultiplier } from "@/utils/price";
import { navigateToEventDetail, skipRoutes } from "@/utils/router";
import { formatUnitPrice, get, getOptionLabel } from "@/utils/utils";

import { AnimatePresence, motion } from "framer-motion";
import { usePathname, useRouter } from "next/navigation";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";

// ============ Type Definitions ============

// NormalEventCard 组件的 ref 类型定义
export interface NormalEventCardRef {
    collectDOMInfo: () => DOMInfo;
}

export interface NormalEventCardProps {
    shadow?: boolean; // Whether to display card shadow
    className?: string;
    onCardClick?: () => void; // Card click event
    eventInfo: any;
    isLive?: boolean; // Whether to display live mode styles
    showLiveLabel?: boolean; // Whether to display LIVE label (only works when isLive is true)
    slice?: number;
    inCard?: boolean;
    imageSize?: "default" | "large"; // Image size: default (40x40) or large (56x56)
    orderType?: OrderType;
}

const DEFAULT_OPTION_PRICE = 50;
type OptionPriceKey = "yesRealtimePrice" | "noRealtimePrice";

function getOptionPrice( guess: any, key: OptionPriceKey ) {
    return get( guess, key, DEFAULT_OPTION_PRICE ) as number | string;
}

function getOptionPercentText( guess: any, key: OptionPriceKey ) {
    return formatCentPriceAsChancePercent( getOptionPrice( guess, key ) );
}

function getOptionOddsText( guess: any, key: OptionPriceKey ) {
    return formatPriceAsOddsMultiplier( getOptionPrice( guess, key ) );
}

// ============ Big Button Component ============
interface BigButtonProps {
    guess: any;
    eventInfo: any;
    registerYesButton?: ( el: HTMLButtonElement | null ) => void;
    orderType?: OrderType;
}

function BigButton( { guess, eventInfo, registerYesButton, orderType }: BigButtonProps ) {
    const { open } = useTradePanel();
    const pathname = usePathname();

    const handleClick = ( value: "yes" | "no" ) => {
        if ( isGuessExpired( guess ) ) return;
        open( {
            guessInfo: guess,
            eventInfo,
            selectedOption: value,
            tradeType: "buy",
            pathname,
            ...( orderType ? { orderType } : {} ),
        } );
    };

    return (
        <>
            <div className="flex-1  overflow-hidden" onClick={( e ) => e.stopPropagation()}>
                <button
                    ref={registerYesButton}
                    onClick={() => handleClick( "yes" )}
                    className={cn(
                        "relative flex w-full gap-1 items-center justify-center rounded h-auto min-h-10 px-5 py-3 text-sm font-medium transition-all border-none",
                        yesBtnCls,
                    )}
                >
                    <div className="truncate max-w-[58px] overflow-hidden text-ellipsis">
                        {getOptionLabel( "yes", guess, eventInfo )}
                    </div>
                    {formatUnitPrice( getOptionPrice( guess, "yesRealtimePrice" ) )}
                </button>

                {/* 赔率 先注释 */}
                {/*<div className="text-center text-xs mt-2">*/}
                {/*    <span className="text-content-subtle">{calc( "yesRealtimePrice | !u:¢ =1", guess )}</span>*/}
                {/*    <span className="mx-1 text-content-subtle">→</span>*/}
                {/*    <span className="text-action-accent">10¢</span>*/}
                {/*</div>*/}
            </div>
            <div className="flex-1 min-w-0 overflow-hidden " onClick={( e ) => e.stopPropagation()}>
                <button
                    onClick={() => handleClick( "no" )}
                    className={cn(
                        "relative flex w-full items-center  gap-1 justify-center rounded h-auto min-h-10 px-5 py-3 text-sm font-medium transition-all border-none",
                        noBtnCls,
                    )}
                >
                    {/* 超出。。。省略 */}
                    <div className="truncate max-w-[58px] overflow-hidden text-ellipsis">
                        {getOptionLabel( "no", guess, eventInfo )}
                    </div>
                    {formatUnitPrice( getOptionPrice( guess, "noRealtimePrice" ) )}
                </button>
                {/* 赔率 先注释 */}
                {/*<div className="text-center text-xs mt-2">*/}
                {/*    <span className="text-content-subtle">{calc( "noRealtimePrice | !u:¢ =1", guess )}</span>*/}
                {/*    <span className="mx-1 text-content-subtle">→</span>*/}
                {/*    <span className="text-action-accent">10¢</span>*/}
                {/*</div>*/}
            </div>
        </>
    );
}

// ============ Main Component ============
export const NormalEventCard = forwardRef<NormalEventCardRef, NormalEventCardProps>(
    function NormalEventCard(
        {
            shadow = true,
            className,
            onCardClick,
            eventInfo,
            isLive = false,
            showLiveLabel = true,
            slice = 999,
            imageSize = "default",
            orderType,
        }: NormalEventCardProps,
        ref: React.ForwardedRef<NormalEventCardRef>,
    ) {
        const router = useRouter();
        const pathname = usePathname();
        const [ expandedLineId, setExpandedLineId ] = useState<string | number | null>( null );
        const [ forceUpdate, setForceUpdate ] = useState( 0 );
        const guessList = eventInfo.guessList;
        const singleGuess = guessList[0] || {};

        const { open } = useTradePanel();

        // 使用 DOM 收集 Hook
        const { registerButton, collectDOMInfo } = useCollectDOMInfo();
        const { renderParts } = useParseText( eventInfo.overview );
        // 判断事件是否已结束：检查 endTime 或 acceptEndTime，任一过期即为过期
        const now = Date.now();
        const isEventEnded = useMemo( () => {
            if ( isEventExpired( eventInfo ) ) {
                return true;
            }
            if ( guessList.length === 1 ) return isGuessExpired( guessList[0] );
            return false;
        }, [ eventInfo, guessList, forceUpdate ] );
        const singleGuessPercentText = getOptionPercentText( singleGuess, "yesRealtimePrice" );
        const singleGuessDisplayText = singleGuessPercentText;

        // Toggle expand state (only one can be expanded)
        const toggleExpand = ( lineId: string | number ) => {
            setExpandedLineId( expandedLineId === lineId ? null : lineId );
        };

        // 暴露 DOM 收集方法给父组件
        useImperativeHandle( ref, () => ( { collectDOMInfo } ) );

        // Handle card click - default navigate to event detail, can be overridden externally
        const handleCardClick = () => {
            if ( skipRoutes.includes( pathname ) ) {
                return;
            }
            if ( onCardClick ) {
                onCardClick();
            } else {
                const eventCategory = get( eventInfo, "eventCategory", undefined );
                void navigateToEventDetail( router, {
                    eventNo: eventInfo.eventNo,
                    isMainPageEvent: true,
                    ...( eventCategory !== undefined && { eventCategory } ),
                } );
            }
        };
        // 普通卡片渲染
        return (
            <div className={cn( baseCardCls, isEventEnded && disableCls, shadowCls, className )}>
                {/* Header area - clickable to navigate */}
                <div className="flex items-center gap-2 cursor-pointer transition-colors" onClick={handleCardClick}>
                    {/* Left icon */}
                    <div className={`flex-shrink-0 rounded-sm overflow-hidden size-[38px]`}>
                        <SmartImage
                            src={eventInfo?.imageUrl
                                || `${process.env.NEXT_PUBLIC_OSS_CDN_URL}/manager/d79d29be139442a6aa3d9b20a1edabf1.jpg`}
                            alt="logo"
                            className="w-full h-full object-cover"
                            imageUrlOptions={{ width: 150, height: 150 }}
                        />
                    </div>

                    {/* Right content */}
                    <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                        {/* Question description */}
                        <p className="text-sm font-semibold text-t1 leading-tight flex-1 line-clamp-3">
                            {eventInfo.title}
                        </p>
                        {/* Percentage - only show for single guess */}
                        {guessList.length === 1 && (
                            <div className={cn( "text-lg text-t1 flex-shrink-0 font-semibold" )}>
                                {singleGuessDisplayText}
                            </div>
                        )}
                    </div>
                </div>

                {/* Content area */}
                <div className="mt-2 mb-3">
                    {guessList.length === 1
                        ? (
                            // ========== Single guess: directly show big buttons ==========
                            <div className="flex gap-2 pt-2">
                                {guessList.map( ( option ) => (
                                    <BigButton
                                        key={option.id}
                                        eventInfo={eventInfo}
                                        guess={guessList[0]}
                                        registerYesButton={( el ) => registerButton( "yes-button", el )}
                                        orderType={orderType}
                                    />
                                ) )}
                            </div>
                        )
                        : guessList.length > 1
                        ? (
                            // ========== Multiple guesses: collapsible list ==========
                            <div className="max-h-[92px] overflow-y-auto scrollbar-transparent flex flex-col gap-2 ">
                                {guessList.slice( 0, slice ).map( ( guess, index ) => {
                                    const isExpanded = expandedLineId === guess.id;
                                    // 判断单个竞猜是否过期
                                    const guessEndTime = get( guess, "endTime", 0 );
                                    const guessAcceptEndTime = get( guess, "acceptEndTime", 0 );
                                    const isGuessEnded = ( guessEndTime && now >= guessEndTime )
                                        || ( guessAcceptEndTime && now >= guessAcceptEndTime );
                                    const guessId = get( guess, "id", index ) as string | number;
                                    const guessPercentText = getOptionPercentText( guess, "yesRealtimePrice" );
                                    const guessOddsText = getOptionOddsText( guess, "yesRealtimePrice" );
                                    const noGuessOddsText = getOptionOddsText( guess, "noRealtimePrice" );
                                    const guessDisplayText = guessPercentText;
                                    const handleClick = ( value: "yes" | "no" ) => {
                                        open( {
                                            guessInfo: guess,
                                            eventInfo,
                                            selectedOption: value,
                                            tradeType: "buy",
                                            pathname,
                                            ...( orderType ? { orderType } : {} ),
                                        } );
                                    };
                                    return (
                                        <div
                                            key={guess.id}
                                            className={cn( "overflow-hidden h-[36px] flex-shrink-0 flex" )}
                                        >
                                            {/* Guess row */}
                                            <div className="flex items-center justify-between gap-2 w-full">
                                                {/* Title */}
                                                <div className="flex-1 text-sm truncate font-normal text-t1">
                                                    {guess.title}
                                                </div>

                                                {/* Percentage + Button 容器 - 使用 transform 动画 */}
                                                <div className="flex items-center gap-2">
                                                    {/* Percentage */}
                                                    <div
                                                        className={cn(
                                                            "text-base font-semibold text-t1 ",
                                                            isGuessEnded && disableCls,
                                                        )}
                                                        style={{
                                                            transform: isExpanded ? "translateX(0)" : "translateX(0)",
                                                            transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                                        }}
                                                    >
                                                        {guessDisplayText}
                                                    </div>

                                                    {/* 收起箭头 - 展开时显示 */}
                                                    {/*<button*/}
                                                    {/*    onClick={( e ) => {*/}
                                                    {/*        e.stopPropagation();*/}
                                                    {/*        toggleExpand( guess.id );*/}
                                                    {/*    }}*/}
                                                    {/*    className="ml-2 flex items-center justify-center w-6 h-6"*/}
                                                    {/*    style={{*/}
                                                    {/*        opacity: isExpanded ? 1 : 0,*/}
                                                    {/*        transform: isExpanded ? "scale(1)" : "scale(0.8)",*/}
                                                    {/*        transition: "opacity 0.2s ease, transform 0.2s ease",*/}
                                                    {/*        pointerEvents: isExpanded ? "auto" : "none",*/}
                                                    {/*    }}*/}
                                                    {/*>*/}
                                                    {/*    <svg*/}
                                                    {/*        width="12"*/}
                                                    {/*        height="8"*/}
                                                    {/*        viewBox="0 0 12 8"*/}
                                                    {/*        fill="none"*/}
                                                    {/*        xmlns="http://www.w3.org/2000/svg"*/}
                                                    {/*    >*/}
                                                    {/*        <path*/}
                                                    {/*            d="M1 6.5L6 1.5L11 6.5"*/}
                                                    {/*            stroke="currentColor"*/}
                                                    {/*            strokeWidth="1.5"*/}
                                                    {/*            strokeLinecap="round"*/}
                                                    {/*            strokeLinejoin="round"*/}
                                                    {/*            className="text-t3"*/}
                                                    {/*        />*/}
                                                    {/*    </svg>*/}
                                                    {/*</button>*/}

                                                    {/* Yes/No 按钮 - 使用 scale + opacity 动画 */}
                                                    {/*<div*/}
                                                    {/*    className="overflow-hidden"*/}
                                                    {/*    style={{*/}
                                                    {/*        width: isExpanded ? 0 : "104px",*/}
                                                    {/*        transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",*/}
                                                    {/*    }}*/}
                                                    {/*>*/}
                                                    {/*    <button*/}
                                                    {/*        ref={( el ) =>*/}
                                                    {/*            registerButton( `expand-button-${guess.id || index}`, el )}*/}
                                                    {/*        onClick={( e ) => {*/}
                                                    {/*            e.stopPropagation();*/}
                                                    {/*            toggleExpand( guess.id );*/}
                                                    {/*        }}*/}
                                                    {/*        className="ml-4 flex items-center"*/}
                                                    {/*        style={{*/}
                                                    {/*            opacity: isExpanded ? 0 : 1,*/}
                                                    {/*            transform: isExpanded ? "scale(0.8)" : "scale(1)",*/}
                                                    {/*            transition: "opacity 0.2s ease, transform 0.2s ease",*/}
                                                    {/*            willChange: "opacity, transform",*/}
                                                    {/*        }}*/}
                                                    {/*    >*/}
                                                    {/*        <div className="relative rounded-lg overflow-hidden h-8 w-[88px]">*/}
                                                    {/*            /!* Yes area *!/*/}
                                                    {/*            <div*/}
                                                    {/*                className={cn('absolute inset-0 flex items-center justify-start', yesBtnCls)}*/}
                                                    {/*                style={{*/}
                                                    {/*                    clipPath: "polygon(0 0, 47px 0, 41px 100%, 0 100%)",*/}
                                                    {/*                }}*/}
                                                    {/*            >*/}
                                                    {/*                <div className="w-[47px] h-full flex items-center justify-center">*/}
                                                    {/*                    <span*/}
                                                    {/*                        className="text-xs font-medium"*/}
                                                    {/*                    >*/}
                                                    {/*                        Yes*/}
                                                    {/*                    </span>*/}
                                                    {/*                </div>*/}
                                                    {/*            </div>*/}

                                                    {/*            /!* No area *!/*/}
                                                    {/*            <div*/}
                                                    {/*                className={cn('absolute inset-0 flex items-center justify-end', noBtnCls)}*/}
                                                    {/*                style={{*/}
                                                    {/*                    clipPath:*/}
                                                    {/*                        "polygon(47px 0, 100% 0, 100% 100%, 41px 100%)",*/}
                                                    {/*                }}*/}
                                                    {/*            >*/}
                                                    {/*                <div className="w-[47px] h-full flex items-center justify-center">*/}
                                                    {/*                    <span*/}
                                                    {/*                        className="text-xs font-medium"*/}
                                                    {/*                    >*/}
                                                    {/*                        No*/}
                                                    {/*                    </span>*/}
                                                    {/*                </div>*/}
                                                    {/*            </div>*/}
                                                    {/*        </div>*/}
                                                    {/*    </button>*/}
                                                    {/*</div>*/}
                                                    {!isGuessEnded && (
                                                        <div className="flex gap-1">
                                                            <button
                                                                onClick={() => handleClick( "yes" )}
                                                                className={cn(
                                                                    yesBtnCls,
                                                                    "font-medium h-9 w-12 rounded flex flex-col items-center justify-center",
                                                                )}
                                                                ref={( el ) =>
                                                                    registerButton(
                                                                        `expand-button-${guess.id || index}`,
                                                                        el,
                                                                    )}
                                                            >
                                                                <div className="text-xs leading-none">Yes</div>
                                                                <div className="text-[10px] leading-none font-semibold mt-0.5">
                                                                    {guessOddsText}
                                                                </div>
                                                            </button>
                                                            <button
                                                                onClick={() => handleClick( "no" )}
                                                                className={cn(
                                                                    noBtnCls,
                                                                    "font-medium h-9 w-12 rounded flex flex-col items-center justify-center",
                                                                )}
                                                            >
                                                                <div className="text-xs leading-none">No</div>
                                                                <div className="text-[10px] leading-none font-semibold mt-0.5">
                                                                    {noGuessOddsText}
                                                                </div>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Expand area */}
                                            <AnimatePresence>
                                                {isExpanded && !isGuessEnded && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: "auto", opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.25, ease: [ 0.4, 0, 0.2, 1 ] }}
                                                        className="overflow-hidden bg-surface-card"
                                                    >
                                                        <div className="flex gap-2 py-3 px-[0] justify-between">
                                                            <BigButton
                                                                key={`big-${guess.id}`}
                                                                eventInfo={eventInfo}
                                                                guess={guess}
                                                                orderType={orderType}
                                                            />
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    );
                                } )}
                            </div>
                        )
                        : null}
                </div>

                {/* News description (optional) - 当多竞猜任意一项展开时隐藏 */}
                {/*<AnimatePresence>*/}
                {/*    {eventInfo.overview && !expandedLineId && (*/}
                {/*        <motion.div*/}
                {/*            initial={{ height: 0, opacity: 0, marginTop: 0 }}*/}
                {/*            animate={{ height: "auto", opacity: 1, marginTop: "24px" }}*/}
                {/*            exit={{ height: 0, opacity: 0, marginTop: 0 }}*/}
                {/*            transition={{ duration: 0.25, ease: [ 0.4, 0, 0.2, 1 ] }}*/}
                {/*            className="overflow-hidden"*/}
                {/*        >*/}
                {/*            <div className="text-sm text-t3 font-[400] leading-relaxed line-clamp-2 break-all">*/}
                {/*                <span className="text-t3 text-sm">News ·</span>*/}
                {/*                {renderParts()}*/}
                {/*            </div>*/}
                {/*        </motion.div>*/}
                {/*    )}*/}
                {/*</AnimatePresence>*/}

                {/* Bottom information */}
                <VolRect
                    eventInfo={eventInfo}
                    showLiveLabel={showLiveLabel}
                    className="mt-3"
                    onCountdownEnd={() => {
                        // 倒计时结束，判断是否过期
                        if (
                            isEventExpired( eventInfo ) || ( guessList.length === 1 && isGuessExpired( guessList[0] ) )
                        ) {
                            // 过期了，触发重新渲染
                            setForceUpdate( ( prev ) => prev + 1 );
                        }
                    }}
                />
            </div>
        );
    },
);
